import { makeStyles } from '@material-ui/styles'
import clsx from 'clsx'
import { insert, insertAll } from 'ramda'
import type { ArrayElement } from 'core/actions/Action'
import DotValueCell from 'core/components/listTable/cells/DotValueCell'
import SimpleLink from 'core/components/SimpleLink'
import TitleWithInfoIcon from 'core/components/TitleWithInfoIcon'
import Badge from 'core/elements/badge/Badge'
import { createGridLinkCell } from 'core/elements/grid/cells/GridLinkCell'
import type { GridViewColumn } from 'core/elements/grid/Grid'
import Text from 'core/elements/Text'
import ColumnInfoTooltip from 'core/elements/tooltip/ColumnInfoTooltip'
import type Theme from 'core/themes/model'
import { routeManager } from 'core/utils/routes'
import { createResourceLabelsCell } from 'k8s/components/common/entity/labels-and-annotations/helpers'
import { IHostDetailsPageTabs } from 'openstack/components/infrastructure/hosts/host-details/model'
import { IVmDetailsPageTabs } from 'openstack/components/vms/vm-details/model'
import { humanReadableSize } from 'openstack/helpers'
import { formatTableDate } from 'utils/misc'
import { HelperTooltipMessages, VOLUME_ERROR_STATES, VOLUME_STATES_TOOLTIPS } from './constants'
import type { volumesSelector } from './selectors'
import { getBadgeText, getBadgeVariant } from './utils/volumeUtils'
import { IVolumeDetailsPageTabs } from './volume-details/model'
import { VolumeSourceCell } from './volume-details/VolumeSourceCell'

export const useStyles = makeStyles<Theme>((theme) => ({
  dropdownAction: {
    background: theme.components.dropdown.background,
    border: 'none',
    '& .button-text': {
      color: theme.components.dropdown.color,
      justifyContent: 'start',
    },
  },
  statusCell: {
    textAlign: 'center',
    display: 'inline-block',
  },
  migrationStatusCell: {
    textAlign: 'left',
  },
  error: {
    '&:hover': {
      cursor: 'pointer',
    },
  },
  filters: {
    display: 'flex',
    gap: 8,
  },
  toggleSwitch: {
    marginBottom: '8px',
  },
  hostLinkCell: { display: 'flex', flexDirection: 'column', gap: '4px' },
}))

export const StatusCellComponent = ({ value, item, setErrorVolume = null }) => {
  const classes = useStyles()
  const migrationStatus = item?.migration_status
  const exposeError = setErrorVolume && VOLUME_ERROR_STATES.includes(value)

  return (
    <div className={clsx(classes.statusCell, exposeError && classes.error)}>
      <Badge
        variant={getBadgeVariant(value)}
        text={getBadgeText(value)}
        onClick={(e) => {
          e.stopPropagation()
          if (exposeError) {
            setErrorVolume(item)
          }
        }}
      />
      {migrationStatus && (
        <Text variant="body2" className={classes.migrationStatusCell}>
          ({migrationStatus})
        </Text>
      )}
    </div>
  )
}

export const HostLinkCell = ({ entries }: { entries: Array<{ name: string; id: string }> }) => {
  const classes = useStyles()

  if (!entries?.length) return null

  return (
    <div className={classes.hostLinkCell}>
      {entries.map(({ name, id }) => (
        <SimpleLink
          key={id ?? name}
          src={routeManager.routes.openstack.hostDetails.path({
            id,
            tab: IHostDetailsPageTabs.Overview,
          })}
        >
          {name}
        </SimpleLink>
      ))}
    </div>
  )
}

export const createHostLinkCell =
  (getEntries: (value: any, item: any) => Array<{ name: string; id: string }>) =>
  ({ value, item }: { value: any; item: any }) => {
    const entries = getEntries(value, item)
    return <HostLinkCell entries={entries} />
  }

export const HypervisorHostsCellComponent = createHostLinkCell((names, item) =>
  (names ?? []).map((name: string, i: number) => ({
    name,
    id: item?.hypervisor_host_ids?.[i],
  })),
)

export const AttachedVMsCellComponents = ({ value: attachedVms }) => {
  if (!attachedVms?.length) return null
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
      {attachedVms.map(({ name, id }) => (
        <SimpleLink
          key={id}
          src={routeManager.routes.openstack.vmDetails.path({
            id: id,
            tab: IVmDetailsPageTabs.Overview,
          })}
        >
          {name || id}
        </SimpleLink>
      ))}
    </div>
  )
}

type SelectorModel = ArrayElement<ReturnType<typeof volumesSelector>>

export const volumeColumns = ({
  hostsById = {},
  isAdmin,
  allTenants,
  setErrorVolume,
}): GridViewColumn<SelectorModel>[] => {
  const columns = [
    {
      key: 'name',
      label: 'Name',
      // width: 'medium',
      CellComponent: createGridLinkCell({
        routeToFn: ({ id, name }) =>
          name
            ? routeManager.routes.openstack.volumeDetails.path({
                id,
                tab: IVolumeDetailsPageTabs.Overview,
              })
            : '',
      }),
    },
    {
      key: 'id',
      label: 'UUID',
      display: false,
      disableSorting: true,
    },
    {
      key: 'status',
      label: (
        <TitleWithInfoIcon
          titleText="Status"
          tooltipMsg={<ColumnInfoTooltip statuses={VOLUME_STATES_TOOLTIPS} />}
        />
      ),
      // render: (status) => statusDisplay[status] || status,
      CellComponent: (props) => <StatusCellComponent {...props} setErrorVolume={setErrorVolume} />,
      disableSorting: true,
    },
    {
      key: 'tenantName',
      label: 'Tenant',
      visible: allTenants,
      disableSorting: true,
    },
    {
      key: 'volume_type',
      label: 'Type',
      disableSorting: true,
    },
    {
      key: 'description',
      label: 'Description',
      disableSorting: true,
    },
    {
      key: 'attachedVms',
      label: 'VM',
      CellComponent: AttachedVMsCellComponents,
      disableSorting: true,
    },
    {
      key: 'device',
      label: 'Device',
      disableSorting: true,
    },
    {
      key: 'snapshots',
      label: 'Snapshots',
      render: (snapshots) => snapshots?.length,
      disableSorting: true,
    },
    {
      key: 'size',
      label: 'Size',
      render: (size) => humanReadableSize(size * 1024 * 1024 * 1024),
    },
    {
      key: 'bootable',
      label: 'Bootable',
      // bootable is string true / false
      render: (val) => <DotValueCell value={val === 'true'} />,
      disableSorting: true,
    },
    {
      key: 'multiattach',
      label: 'Multiattach',
      // multiattach is boolean
      render: (val) => <DotValueCell value={val} />,
      disableSorting: true,
    },
    {
      key: 'source',
      label: (
        <TitleWithInfoIcon
          titleText="Source"
          tooltipMsg={'The resource used to create this volume. Blank if no resource used'}
        />
      ),
      CellComponent: VolumeSourceCell,
      disableSorting: true,
    },
    {
      key: 'created_at',
      label: 'Created',
      formatFn: (value) => (value ? formatTableDate(value) : null),
    },
    {
      key: 'updated_at',
      label: 'Updated At',
      display: false,
      formatFn: (value) => (value ? formatTableDate(value) : null),
      disableSorting: true,
    },
    {
      key: 'metadata',
      label: 'Metadata',
      CellComponent: createResourceLabelsCell({ type: 'table', separator: '=' }),
      disableSorting: true,
    },
    {
      key: 'volume_image_metadata',
      label: 'Image Metadata',
      display: false,
      CellComponent: createResourceLabelsCell({ type: 'table', separator: '=' }),
      disableSorting: true,
    },
  ]
  if (isAdmin()) {
    // @ts-ignore
    const updateColumns = insertAll(
      3,
      [
        {
          key: 'hostId',
          label: (
            <TitleWithInfoIcon
              titleText="Management Host"
              tooltipMsg={HelperTooltipMessages.ManagementHost}
            />
          ),
          CellComponent: createHostLinkCell((hostId, item) => {
            const name = item?.hostName || hostsById?.[hostId]?.name || hostId
            return name ? [{ name, id: hostId }] : []
          }),
          disableSorting: true,
        },
        {
          key: 'backend',
          label: 'Volume Backend',
          disableSorting: true,
        },
      ],
      columns,
    )

    // Insert hypervisor host names column after VM column
    return insert(
      9,
      {
        key: 'hypervisor_host_names',
        label: (
          <TitleWithInfoIcon
            titleText="Hypervisor Host"
            tooltipMsg={HelperTooltipMessages.HypervisorHost}
          />
        ),
        CellComponent: HypervisorHostsCellComponent,
        disableSorting: true,
      },
      updateColumns,
    )
  }
  return columns
}
